<template>
    <div class="ad-card">
        <div class="ad-card__header">
            <div class="ad-card__info">
                <h4 class="ad-card__title">{{ ad.name || 'Объявление #' + ad.id }}</h4>
                <div class="ad-card__meta">
                    <span class="ad-card__id">ID: {{ ad.id }}</span>
                    <span class="ad-card__period">{{ formatPeriod(ad.period_start, ad.period_end) }}</span>
                </div>
            </div>
            <div class="ad-card__status">
                <span :class="['status', 'status--' + normalizeStatus(ad.status)]">
                    <span class="status__text">{{ statusLabel(ad.status) }}</span>
                </span>
            </div>
        </div>
        
        <div class="ad-card__content">
            <div class="ad-card__creatives">
                <div class="section-header">
                    <span class="section-header__title">Креативы</span>
                </div>
                <div v-if="(ad.creatives || []).length" class="creatives-grid">
                    <div
                        v-for="cr in ad.creatives"
                        :key="cr.id"
                        class="creative-card"
                        role="button"
                        tabindex="0"
                        @click="openPreview(cr)"
                        @keydown.enter.prevent="openPreview(cr)"
                    >
                        <div class="creative-card__media">
                            <template v-if="isImage(creativeSrc(cr))">
                                <img
                                    :src="creativeSrc(cr)"
                                    :alt="cr.name || 'ID ' + cr.id"
                                    loading="lazy"
                                    @load="(e) => updateImageMeta(cr, e)"
                                />
                            </template>
                            <template v-else-if="isVideo(creativeSrc(cr))">
                                <video
                                    :src="creativeSrc(cr)"
                                    muted
                                    playsinline
                                    preload="metadata"
                                    class="creative-card__video"
                                    @loadedmetadata="(e) => updateVideoMeta(cr, e)"
                                ></video>
                            </template>
                            <template v-else>
                                <div class="creative-card__placeholder">
                                    <span class="creative-card__placeholder-text">{{ cr.name || 'ID ' + cr.id }}</span>
                                </div>
                            </template>
                        </div>
                        <div class="creative-card__info" v-if="captionFor(cr)">
                            <span class="creative-card__caption">{{ captionFor(cr) }}</span>
                        </div>
                    </div>
                </div>
                <div v-else class="empty-state">
                    <span class="empty-state__text">Нет креативов</span>
                </div>
            </div>

            <div class="ad-card__inventories">
                <div class="section-header">
                    <span class="section-header__title">Инвентари</span>
                </div>
                <div v-if="(ad.inventories || []).length" class="inventory-tags">
                    <span
                        class="inventory-tag"
                        v-for="(inv, ii) in ad.inventories"
                        :key="ii"
                        :title="String(inv && (inv.gid || inv.id || inv))"
                    >
                        {{ String(inv && (inv.gid || inv.id || inv)) }}
                    </span>
                </div>
                <div v-else class="empty-state">
                    <span class="empty-state__text">Нет инвентарей</span>
                </div>
            </div>

            <div class="ad-card__times">
                <div class="section-header">
                    <span class="section-header__title">Время показа</span>
                </div>
                <div v-if="groupedTimeSettings.length" class="time-schedule">
                    <div v-for="(dayGroup, di) in groupedTimeSettings" :key="di" class="time-slot">
                        <span class="time-slot__day">{{ dayName(dayGroup.day) }}</span>
                        <div class="time-slot__ranges">
                            <span v-for="(t, ti) in dayGroup.times" :key="ti" class="time-slot__range">
                                {{ msToHM(t.start_time) }} — {{ msToHM(t.end_time) }}
                            </span>
                        </div>
                    </div>
                </div>
                <div v-else class="empty-state">
                    <span class="empty-state__text">Нет настроек времени</span>
                </div>
            </div>
        </div>
        
        <div class="ad-card__actions">
            <button
                class="action-btn action-btn--primary"
                @click="goToEdit"
                title="Редактировать объявление"
            >
                <span class="action-btn__text">Редактировать</span>
            </button>
            <button
                class="action-btn action-btn--danger"
                :disabled="!canCancelAd(ad) || isAdCancellingId === ad.id"
                @click="$emit('cancel-click', ad)"
                title="Отменить объявление"
            >
                <span class="action-btn__text">Отменить</span>
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AdCard',
    props: {
        ad: {
            type: Object,
            required: true,
        },
        campaignId: {
            type: [Number, String],
            required: true,
        },
        normalizeStatus: {
            type: Function,
            required: true,
        },
        statusLabel: {
            type: Function,
            required: true,
        },
        formatPeriod: {
            type: Function,
            required: true,
        },
        dayName: {
            type: Function,
            required: true,
        },
        msToHM: {
            type: Function,
            required: true,
        },
        canCancelAd: {
            type: Function,
            required: true,
        },
        isAdCancellingId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            creativeMeta: {},
        };
    },
    computed: {
        sortedTimeSettings() {
            const timeSettings = this.ad.time_settings || [];
            // Сортируем по дням недели: понедельник (1) - воскресенье (7)
            return [...timeSettings].sort((a, b) => {
                const dayA = Number(a.day) || 0;
                const dayB = Number(b.day) || 0;
                return dayA - dayB;
            });
        },
        groupedTimeSettings() {
            const timeSettings = this.sortedTimeSettings;
            const grouped = {};
            
            // Группируем по дням недели
            timeSettings.forEach(t => {
                const day = Number(t.day) || 0;
                if (!grouped[day]) {
                    grouped[day] = {
                        day: day,
                        times: []
                    };
                }
                grouped[day].times.push(t);
            });
            
            // Преобразуем объект в массив и сортируем по дням
            return Object.values(grouped).sort((a, b) => a.day - b.day);
        },
    },
    methods: {
        goToEdit() {
            window.location.href = `/guaranteed/campaigns/${this.campaignId}/advertisements/${this.ad.id}/edit`;
        },
        creativeSrc(cr) {
            if (!cr) return null;
            return cr.preview || cr.url || cr.src || null;
        },
        isImage(src) {
            if (!src || typeof src !== 'string') return false;
            return /\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/i.test(src);
        },
        isVideo(src) {
            if (!src || typeof src !== 'string') return false;
            return /\.(mp4|webm|ogg)(\?.*)?$/i.test(src);
        },
        openPreview(cr) {
            const src = this.creativeSrc(cr);
            if (!src) return;
            this.$emit('preview-open', { src, name: cr && (cr.name || `ID ${cr.id}`) });
        },
        updateImageMeta(cr, evt) {
            const img = evt && evt.target;
            if (!img || !cr) return;
            const key = cr.id ?? this.creativeSrc(cr);
            const next = {
                ...this.creativeMeta,
                [key]: {
                    type: 'image',
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                    sizeBytes: cr.size || cr.filesize || null,
                },
            };
            this.creativeMeta = next;
        },
        updateVideoMeta(cr, evt) {
            const el = evt && evt.target;
            if (!el || !cr) return;
            const key = cr.id ?? this.creativeSrc(cr);
            const next = {
                ...this.creativeMeta,
                [key]: {
                    type: 'video',
                    durationSec: el.duration || null,
                    sizeBytes: cr.size || cr.filesize || null,
                },
            };
            this.creativeMeta = next;
        },
        captionFor(cr) {
            if (!cr) return '';
            const key = cr.id ?? this.creativeSrc(cr);
            const meta = this.creativeMeta[key] || {};
            const parts = [];
            if (meta.type === 'image') {
                if (meta.width && meta.height) parts.push(`${meta.width}×${meta.height}`);
            }
            if (meta.type === 'video') {
                if (meta.durationSec) parts.push(this.formatDuration(meta.durationSec));
            }
            const sz = cr.size || cr.filesize || meta.sizeBytes;
            if (Number(sz) > 0) parts.push(this.formatBytes(Number(sz)));
            return parts.join(' · ');
        },
        formatBytes(bytes) {
            const thresh = 1024;
            if (!Number.isFinite(bytes) || bytes <= 0) return '';
            const units = ['KB', 'MB', 'GB', 'TB'];
            let u = -1;
            let v = bytes;
            do {
                v /= thresh;
                ++u;
            } while (v >= thresh && u < units.length - 1);
            return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[u]}`;
        },
        formatDuration(sec) {
            const total = Math.round(sec || 0);
            const h = Math.floor(total / 3600);
            const m = Math.floor((total % 3600) / 60);
            const s = total % 60;
            if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
            return `${m}:${String(s).padStart(2, '0')}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.ad-card {
    background: var(--bg);
    border: 1px solid var(--card-border);
    border-radius: calc(var(--radius) - 4px);
    padding: 12px;
    transition: all 0.2s ease;

    &:hover {
        border-color: #cbd5e1;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    }

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        padding-bottom: 6px;
        border-bottom: 0.5px solid #d0d5dd;
    }

    &__info {
        flex: 1;
    }

    &__title {
        font-size: 12px;
        font-weight: 600;
        color: var(--accent);
        margin: 0;
    }

    &__meta {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    &__id {
        font-size: 10px;
        color: var(--muted);
        font-family: monospace;
    }

    &__period {
        font-size: 10px;
        color: var(--muted);
        font-weight: 400;
    }

    &__status {
        margin-left: 16px;
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    &__actions {
        margin-top: 8px;
        padding-top: 6px;
        border-top: 1px solid rgba(15, 23, 42, 0.08);
        display: flex;
        justify-content: flex-end;
        gap: 8px;
    }

    // Креативы
    .creatives-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
        gap: 3px;
    }

    .creative-card {
        background: rgba(15, 23, 42, 0.05);
        border: 1px solid rgba(15, 23, 42, 0.08);
        border-radius: 6px;
        overflow: hidden;
        cursor: pointer;
        transition: all 0.2s ease;
        display: block !important;
        flex: none !important;
        max-width: none !important;

        &:hover {
            border-color: #cbd5e1;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
        }

        &__media {
            position: relative;
            aspect-ratio: 16/9;
            overflow: hidden;

            img,
            &.creative-card__video {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }

        &__placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 100%;
            background: rgba(15, 23, 42, 0.03);
            color: var(--muted);

            &-text {
                font-size: 8px;
                text-align: center;
                padding: 0 4px;
            }
        }

        &__info {
            padding: 2px 4px;
            background: var(--bg);
        }

        &__caption {
            font-size: 8px;
            color: var(--muted);
            font-weight: 400;
        }
    }

    // Специальные стили для секций
    &__creatives {
        .section-header {
            border-top: none;
        }
    }

    &__times {
        .section-header {
            border-bottom: none;
        }
    }
}

// Статус бейджи
.status-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    // Положительные статусы
    &--active,
    &--activated,
    &--booked,
    &--confirmed,
    &--reserved {
        background: linear-gradient(135deg, #10b981, #059669);
        color: white;
    }

    // Статусы на рассмотрении
    &--approve,
    &--review,
    &--reserve {
        background: linear-gradient(135deg, #f59e0b, #d97706);
        color: white;
    }

    // Пауза
    &--pause {
        background: linear-gradient(135deg, #8b5cf6, #7c3aed);
        color: white;
    }

    // Возобновление
    &--resume {
        background: linear-gradient(135deg, #06b6d4, #0891b2);
        color: white;
    }

    // Отрицательные статусы
    &--rejected,
    &--cancelled {
        background: linear-gradient(135deg, #ef4444, #dc2626);
        color: white;
    }
}

// Заголовки секций
.section-header {
    margin-top: 8px;
    padding-top: 6px;
    margin-bottom: 4px;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);

    &__title {
        font-size: 10px;
        font-weight: 600;
        color: var(--accent);
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
}

// Инвентари
.inventory-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
}

.inventory-tag {
    display: inline-block;
    padding: 2px 6px;
    background: rgba(15, 23, 42, 0.05);
    color: var(--muted);
    border-radius: 4px;
    font-size: 10px;
    font-weight: 400;
    font-family: monospace;
}

// Время показа
.time-schedule {
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.time-slot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 8px;
    background: linear-gradient(135deg, rgba(34, 197, 94, 0.08), rgba(134, 239, 172, 0.05));
    border: 1px solid rgba(34, 197, 94, 0.15);
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
        background: linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(134, 239, 172, 0.08));
        border-color: rgba(34, 197, 94, 0.25);
    }

    &__day {
        font-size: 10px;
        font-weight: 600;
        color: var(--accent);
        align-self: center;
        flex-shrink: 0;
        margin-right: 12px;
    }

    &__ranges {
        display: flex;
        flex-direction: column;
        gap: 2px;
        flex: 1;
        align-items: flex-end;
    }

    &__range {
        font-size: 10px;
        color: var(--muted);
        font-family: monospace;
        font-weight: 500;
    }
}

// Пустые состояния
.empty-state {
    text-align: center;
    padding: 8px;
    color: var(--muted);
    font-style: italic;

    &__text {
        font-size: 10px;
    }
}

// Кнопки действий
.action-btn {
    display: flex;
    align-items: center;
    padding: 4px 8px;
    border: none;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;

    &--primary {
        background: linear-gradient(135deg, rgba(0, 166, 255, 1), rgba(0, 120, 255, 0.9));
        color: white;

        &:hover:not(:disabled) {
            background: linear-gradient(135deg, rgba(0, 120, 255, 1), rgba(0, 100, 255, 0.9));
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0, 166, 255, 0.3);
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    &--danger {
        background: linear-gradient(135deg, #ef4444, #dc2626);
        color: white;

        &:hover:not(:disabled) {
            background: linear-gradient(135deg, #dc2626, #b91c1c);
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }

    &__text {
        font-size: 10px;
    }
}

// Дополнительные элементы
.type-label {
    display: inline-block;
    padding: 2px 6px;
    background: rgba(15, 23, 42, 0.05);
    color: var(--accent);
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

// Адаптивность
@media (max-width: 768px) {
    .creatives-grid {
        grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
    }
}
</style>
